import { useEffect, useState, useCallback, useMemo } from 'react';
import { Box, Divider, IconButton, InputAdornment, InputBase, MenuItem, Select, Stack } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { NearestSalesPoint, SalesPointType } from 'orderme-api-integration-client';
import { SalePointClient } from '../../helpers/client';
import { NavBar } from '../appBars/navBar/navBar';
import { FilterCard, FilterLabel } from './pointSelectionStyles';
import { Linear } from '../progressBars/linear';
import { ChoosePoints } from './choosePoints';
import { NoChoosePoint } from './noChoosePoint';
import { BottomCartBar } from '../appBars/cartBar/bottomCartBar';
import latinize from 'latinize';
import { useTranslation } from 'react-i18next';
import { AppStyle } from '../../helpers/appStyle';
import { appStyleMode } from '../../helpers/clientConfigs';
import { BreadcrumbsBar } from '../breadcrumbs/breadcrumbsBar';
import { useRouteNavigator } from '../../routes/useRouteNavigator';
import { SalesPointsRoute } from '../../routes/allRoutes';
import { useGeolocation } from '../../hooks/useGeolocation';

export enum FilterTypes {
  SalesPoint = 'SalesPoint',
  Wardrobe = 'Wardrobe',
  All = 'All',
}

interface PointSelectionProps {
  filterType?: FilterTypes;
}

export function PointSelection(props: PointSelectionProps) {
  const routeNavigator = useRouteNavigator();
  const { t } = useTranslation();
  const salesPointClient = useMemo(() => new SalePointClient(), []);
  const [salesPoints, setSalesPoints] = useState<NearestSalesPoint[]>();
  const [filteredSalesPoints, setFilteredSalesPoints] = useState<NearestSalesPoint[]>();
  const [selectedType, setSelectedType] = useState<FilterTypes>(props.filterType ?? FilterTypes.All);
  const [cities, setCities] = useState<string[]>();
  const [selectedCity, setSelectedCity] = useState<string>(FilterTypes.All);
  const [search, setSearch] = useState('');

  const getPoints = useCallback(() => {
    salesPointClient
      .getByType(SalesPointType.SalesPoint)
      .then((response) => {
        var points = response.map(
          (point) =>
            new NearestSalesPoint({
              value: point,
              distanceInDegrees: undefined,
              distanceInMeters: undefined,
            })
        );

        setSalesPoints(points);

        var responseCities = response.map((res) => res.city);
        var uniqueCities = responseCities.filter((c, index) => {
          return responseCities.indexOf(c) === index;
        });
        setCities(uniqueCities);
      })
      .catch((err) => {
        console.log(err);
      });
  }, [salesPointClient]);

  const getPointsByLocation = useCallback(
    (longitude: number, latitude: number) => {
      salesPointClient
        .getByLocation(longitude, latitude)
        .then((response) => {
          console.log(response);
          const filteredByDistance = response
            .filter((point) => point.value.type === SalesPointType.SalesPoint)
            .sort((a, b) => a.distanceInMeters - b.distanceInMeters);
          setSalesPoints(filteredByDistance);

          var responseCities = response.map((res) => res.value.city);
          var uniqueCities = responseCities.filter((c, index) => {
            return responseCities.indexOf(c) === index;
          });
          setCities(uniqueCities);
        })
        .catch((err) => {
          console.log(err);
        });
    },
    [salesPointClient]
  );

  const { requestLocation } = useGeolocation({
    onLocation: getPointsByLocation,
    onPermissionDenied: getPoints,
  });

  const filterSalePoints = useCallback(() => {
    const filteredTypes = salesPoints?.filter(
      (point) => selectedType === FilterTypes.All || point.value.type === (selectedType as string)
    );
    const filteredPoints = filteredTypes?.filter(
      (point) => selectedCity === FilterTypes.All || point.value.city === selectedCity
    );
    const term = latinize(search).toLowerCase();

    const searchedDeliveries: NearestSalesPoint[] = filteredPoints?.filter((deliveryDetail) => {
      const { address, name } = deliveryDetail.value;
      return (
        latinize(address || '')
          .toLowerCase()
          .includes(term) ||
        latinize(name || '')
          .toLowerCase()
          .includes(term)
      );
    });

    setFilteredSalesPoints(searchedDeliveries);
  }, [salesPoints, selectedCity, selectedType, search]);

  useEffect(() => {
    requestLocation();
  }, [requestLocation]);

  useEffect(() => {
    filterSalePoints();
  }, [filterSalePoints]);

  const handleSalesPointChange = (e: any) => {
    const value = e.target.value;
    setSelectedType(value);
  };

  const handleCityChange = (e: any) => {
    const value = e.target.value;
    setSelectedCity(value);
  };

  const continueToPurchase = () => {
    routeNavigator.goToCart();
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
      <NavBar barTitle={t('salesPoint:salesPoints')} />
      <BreadcrumbsBar paths={[{ path: SalesPointsRoute }]} />
      {filteredSalesPoints === undefined && <Linear />}
      {filteredSalesPoints && (
        <Box sx={{ maxWidth: '1000px', width: '100%' }}>
          {appStyleMode === AppStyle.Mobile && (
            <Stack sx={{ mb: '10px' }} justifyContent="space-between">
              <Box>
                <IconButton disabled>
                  <SearchIcon />
                </IconButton>
                <InputBase
                  sx={{ width: '160px' }}
                  placeholder={t('salesPoint:search')}
                  onChange={(e) => setSearch(e.target.value)}
                />
              </Box>
              <Divider sx={{ width: '100%' }} />
            </Stack>
          )}
          <>
            {salesPoints?.length > 1 && (
              <>
                <Stack sx={{ ml: '16px', width: 'auto' }} direction="row" justifyContent={'space-between'}>
                  <FilterCard>
                    <FilterLabel>{(t('salesPoint:point') as string).toUpperCase()}</FilterLabel>
                    <Stack direction="row" justifyContent="center" alignItems="center">
                      <Select
                        sx={{ color: 'button.primary.color' }}
                        fullWidth
                        variant="standard"
                        value={selectedType}
                        onChange={handleSalesPointChange}
                        SelectDisplayProps={{
                          style: { display: 'flex', justifyContent: 'center', paddingLeft: '15px' },
                        }}
                        disableUnderline
                      >
                        <MenuItem value={FilterTypes.All}>{t('salesPoint:all')}</MenuItem>
                        <MenuItem value={FilterTypes.SalesPoint}>{t('salesPoint:salesPoint')}</MenuItem>
                        <MenuItem value={SalesPointType.Wardrobe}>{t('salesPoint:wardrobe')}</MenuItem>
                      </Select>
                    </Stack>
                  </FilterCard>
                  <FilterCard sx={{ marginRight: '16px' }}>
                    <FilterLabel>{(t('salesPoint:city') as string).toUpperCase()}</FilterLabel>
                    <Stack direction="row" justifyContent="center" alignItems="center">
                      <Select
                        sx={{ color: 'button.primary.color' }}
                        SelectDisplayProps={{
                          style: { display: 'flex', justifyContent: 'center' },
                        }}
                        fullWidth
                        variant="standard"
                        value={selectedCity}
                        onChange={handleCityChange}
                        disableUnderline
                      >
                        <MenuItem value={FilterTypes.All}>{t('salesPoint:all')}</MenuItem>
                        {cities?.map((city, index) => {
                          return (
                            <MenuItem key={index} value={city}>
                              {city}
                            </MenuItem>
                          );
                        })}
                      </Select>
                    </Stack>
                  </FilterCard>
                </Stack>
              </>
            )}
            {appStyleMode !== AppStyle.Mobile && salesPoints?.length > 1 && (
              <Box sx={{ display: 'flex', height: '55px', marginTop: '3px' }}>
                <InputBase
                  fullWidth
                  sx={{
                    color: 'black',
                    backgroundColor: 'white',
                    mx: '16px',
                    px: '10px',
                    borderRadius: '4px',
                  }}
                  placeholder={t('salesPoint:search')}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  startAdornment={
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  }
                  endAdornment={
                    <InputAdornment position="end">
                      <ClearIcon
                        sx={{ visibility: search ? 'visible' : 'hidden', cursor: 'pointer' }}
                        onClick={() => setSearch('')}
                      />
                    </InputAdornment>
                  }
                />
              </Box>
            )}
            {filteredSalesPoints !== undefined && filteredSalesPoints.length === 0 ? (
              <NoChoosePoint />
            ) : (
              <ChoosePoints filteredDeliveryDetails={filteredSalesPoints} expanded={salesPoints?.length === 1} />
            )}
          </>
        </Box>
      )}
      {appStyleMode === AppStyle.Mobile && <BottomCartBar open={continueToPurchase} />}
    </Box>
  );
}
